import React, { useMemo, useState } from 'react';
import { Button, Icon } from '@iq/react-components';
import { parseISO, format } from 'date-fns';

import { formatBytes, getFilenameWithoutExt } from '../../../../../utils';

import { DropdownMenu } from '../../../../DropdownMenu';
import Pill from '../../../../Pill';
import FileListBookmarks from '../FileListBookmarks';
import FiletypeIcon from '../../../../FiletypeIcon';
import LabelWithTooltip from '../../../../LabelWithTooltip';

const ComponentLabel = ({ entries }) => {
  if (entries.length === 0) return <span>{'\u2014'}</span>;

  const tooltipContent =
    entries.length > 1
      ? entries.map((entry, i) => (
          <span
            key={i}
            style={{ display: 'block', marginBottom: i < entries.length - 1 ? 4 : 0 }}
          >
            {entry.full}
          </span>
        ))
      : entries[0].full;

  return (
    <span className="component-cell">
      <LabelWithTooltip
        name={entries[0].full}
        tooltipContent={tooltipContent}
        forceTooltip={entries.length > 1}
      />
      {entries.length > 1 && <span className="component-more">(+{entries.length - 1} more)</span>}
    </span>
  );
};

const TableRow = ({
  showFileExtension,
  filterValue,
  tagFilter,
  activeComponentId,
  componentMap,
  visibleBookmarkLists,
  onToggleBookmarkList,
  disableSelect,
  isShiftPressed,
  onPreview,
  onSelectFile,
  file,
  getDropdownOptions = () => [],
}) => {
  const hideFileExtension = (filename) => getFilenameWithoutExt(filename) || filename;

  const onClickFile = (f) => {
    if (isShiftPressed) {
      onSelectFile(f);
    } else {
      onPreview(f);
    }
  };

  const componentEntries = (file.components || []).map((id) => componentMap[id]).filter(Boolean);

  const options = getDropdownOptions(file);
  return (
    <div className="file-table-row">
      {!disableSelect && (
        <div className="table-cell select-column">
          <Button
            design="text"
            tooltip="Select"
          >
            <Icon
              className={`checkbox ${file.isSelected ? 'checked' : ''}`}
              icon={`${file.isSelected ? 'he-checkbox-selected' : 'he-checkbox'}`}
              size="s"
              onClick={(e) => {
                e.stopPropagation();
                onSelectFile(file);
              }}
            />
          </Button>
        </div>
      )}

      <div className="table-cell filename-column">
        <div className="filename">
          <FiletypeIcon
            file={file}
            onClick={() => onClickFile(file)}
          />
          <span
            className="filename-text"
            onClick={() => onClickFile(file)}
          >
            {showFileExtension ? file.filename : hideFileExtension(file.filename)}
          </span>
          {file.bookmarks.length > 0 && (
            <Pill
              icon="bookmark"
              text={file.bookmarks.length}
              onClick={() => onToggleBookmarkList(file.id)}
            />
          )}
        </div>
        <FileListBookmarks
          file={file}
          onPreview={onPreview}
          filterValue={filterValue}
          bookmarks={file.bookmarks}
          tagFilter={tagFilter}
          activeComponentId={activeComponentId}
          bookmarkListToggled={visibleBookmarkLists.indexOf(file.id) !== -1}
        />
      </div>

      <div className="table-cell type-column">
        <span className="type-badge">{file.type}</span>
      </div>

      <div className="table-cell components-column">
        <ComponentLabel entries={componentEntries} />
      </div>

      <div className="table-cell size-column">
        <span>{formatBytes(file.size)}</span>
      </div>

      <div className="table-cell created-at-column">
        <span>{format(parseISO(file.createdAt), 'yyyy-MM-dd')}</span>
      </div>

      <div className="table-cell actions-column">
        {!!options.length && (
          <DropdownMenu
            trigger="he-moreoptionsvertical"
            menuOptions={options}
            menuXPlacement="left"
          />
        )}
      </div>
    </div>
  );
};

const FileTable = ({
  files,
  tagFilter,
  activeComponentId,
  components,
  filterValue,
  showFileExtension,
  getDropdownOptions,
  onPreview,
  onSelectFile,
  isShiftPressed,
  disableSelect,
  setSortBy,
  sortBy,
  setOrder,
  order,
}) => {
  const [visibleBookmarkLists, setVisibleBookmarkLists] = useState([]);

  const componentMap = useMemo(
    () =>
      (components || []).reduce((acc, c) => {
        acc[c.id] = {
          short: c.name,
          full: `[ ${c.itemDesignation} ] ${c.name}`,
        };
        return acc;
      }, {}),
    [components]
  );

  const tableHeaders = [
    ['Filename', 'filename-column', 'filename'],
    ['Type', 'type-column', 'type'],
    ['Components', 'components-column', null],
    ['Size', 'size-column', 'size'],
    ['Created at', 'created-at-column', 'createdAt'],
  ];

  const sortList = (sortOption) => {
    if (sortOption === sortBy) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
      return;
    }
    setSortBy(sortOption);
    setOrder(sortOption !== 'filename' ? 'desc' : 'asc');
  };

  const sortingIcon = (fileSortOption) => {
    if (sortBy !== fileSortOption) {
      return 'he-down';
    }
    if (order === 'asc') {
      return 'he-up';
    }
    return 'he-down';
  };

  const onToggleBookmarkList = (fileId) => {
    setVisibleBookmarkLists((currentValue) =>
      currentValue.indexOf(fileId) !== -1
        ? currentValue.filter((id) => id !== fileId)
        : [...currentValue, fileId]
    );
  };

  return (
    <div className="file-table-component">
      <div className="file-table-table">
        <div className="file-table-headers">
          {!disableSelect && <div className="table-header select-column"></div>}
          {tableHeaders.map((header) => (
            <div
              key={header[0]}
              className={`table-header ${header[1]}`}
            >
              <Button
                design="text"
                onClick={() => header[2] && sortList(header[2])}
              >
                {header[0]}
                {header[2] && (
                  <Icon
                    className={sortBy !== header[2] ? 'hoverableIcon' : undefined}
                    size="xs"
                    icon={sortingIcon(header[2])}
                  />
                )}
              </Button>
            </div>
          ))}
          <div className="table-header actions-column"></div>
        </div>
        {files.map((file) => (
          <TableRow
            showFileExtension={showFileExtension}
            filterValue={filterValue}
            tagFilter={tagFilter}
            activeComponentId={activeComponentId}
            componentMap={componentMap}
            visibleBookmarkLists={visibleBookmarkLists}
            onToggleBookmarkList={onToggleBookmarkList}
            disableSelect={disableSelect}
            isShiftPressed={isShiftPressed}
            onPreview={onPreview}
            onSelectFile={onSelectFile}
            file={file}
            getDropdownOptions={getDropdownOptions}
            key={file.id}
          />
        ))}
      </div>
    </div>
  );
};

export default FileTable;
